import OpenAI from 'openai';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

type ApiKeys = {
  openai?: string;
  claude?: string;
  google?: string;
};

// Helper to check user subscription
async function getUserSubscription(): Promise<{ isPro: boolean; subscription: string }> {
  const user = auth.currentUser;
  if (!user) return { isPro: false, subscription: 'free' };
  
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const subscription = userDoc.data()?.subscription || 'free';
    const isPro = ['pro', 'premium', 'enterprise'].includes(subscription);
    return { isPro, subscription };
  } catch {
    return { isPro: false, subscription: 'free' };
  }
}

export async function enhancePromptPro(prompt: string, apiKeys?: ApiKeys): Promise<string | null> {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKeys?.openai;
  if (isPro && !effectiveApiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) {
    // Try Google as fallback
    if (apiKeys?.google || (isPro && process.env.REACT_APP_GOOGLE_AI_API_KEY)) {
      return enhanceWithGemini(prompt, apiKeys?.google || process.env.REACT_APP_GOOGLE_AI_API_KEY);
    }
    return null;
  }

  try {
    const openai = new OpenAI({ 
      apiKey: effectiveApiKey,
      dangerouslyAllowBrowser: true 
    });

    const response = await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: `You are an expert prompt engineer specializing in AI image and video generation. 
          Your task is to enhance prompts to make them more detailed, creative, and effective.
          
          Guidelines:
          - Add artistic details (lighting, composition, style)
          - Include technical specifications when appropriate
          - Enhance visual descriptions with cinematic terms
          - Maintain the original intent while making it more professional
          - Keep the enhanced prompt concise but impactful
          - Do not add platform-specific parameters (--ar, --v, etc.)
          
          Return ONLY the enhanced prompt text, nothing else.`
        },
        {
          role: "user",
          content: `Enhance this prompt for better results: "${prompt}"`
        }
      ],
      max_tokens: 500,
      temperature: 0.7
    });

    return response.choices[0]?.message?.content || null;
  } catch (error) {
    console.error('OpenAI enhancement failed:', error);
    
    // Try Google as fallback
    if (apiKeys?.google || (isPro && process.env.REACT_APP_GOOGLE_AI_API_KEY)) {
      return enhanceWithGemini(prompt, apiKeys?.google || process.env.REACT_APP_GOOGLE_AI_API_KEY);
    }
  }

  return null;
}

async function enhanceWithGemini(prompt: string, apiKey?: string): Promise<string | null> {
  if (!apiKey) return null;
  
  try {
    // Dynamic import to avoid loading if not needed
    const { GoogleGenerativeAI } = await import('@google/generative-ai');
    const genAI = new GoogleGenerativeAI(apiKey);
    const model = genAI.getGenerativeModel({ model: "gemini-pro" });

    const enhancementPrompt = `You are an expert prompt engineer. Enhance this prompt for AI image/video generation, making it more detailed and creative while maintaining its original intent. Add artistic details like lighting, composition, and style. Return ONLY the enhanced prompt text.

Original prompt: "${prompt}"

Enhanced prompt:`;

    const result = await model.generateContent(enhancementPrompt);
    const response = await result.response;
    return response.text();
  } catch (error) {
    console.error('Gemini enhancement failed:', error);
    return null;
  }
}

export async function optimizeForPlatformPro(
  prompt: string, 
  platform: 'midjourney' | 'dalle' | 'stable-diffusion' | 'google-veo' | 'midjourney-video' | 'kling-ai',
  apiKeys?: ApiKeys
): Promise<string | null> {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKeys?.openai;
  if (isPro && !effectiveApiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) return null;

  const platformGuidelines = {
    midjourney: 'For Midjourney v7: Focus on photorealistic details, cinematic composition, specific camera/lens info. Use parameters: --v 7 --style raw for photorealism, --stylize for artistic control, --chaos for variation, --weird for unconventional results.',
    dalle: 'For DALL-E 3: Be extremely specific and detailed. Use natural language descriptions. Focus on composition, lighting, and style. Avoid technical parameters.',
    'stable-diffusion': 'For SDXL: Include quality tags (masterpiece, best quality), artistic style, detailed descriptions. Add negative prompts. Use Steps:30, CFG:7 for balance.',
    'google-veo': 'For Google Veo: Specify camera movements (tracking, panning, dolly), frame rate (24/30/60 fps), duration in seconds, resolution (4K/8K), and cinematic style.',
    'midjourney-video': 'For Midjourney Video: Focus on motion dynamics, temporal consistency, smooth transitions. Use --video flag, --motion parameter (60-360), --fps for frame rate. Describe movement patterns and flow.',
    'kling-ai': 'For Kling AI: Emphasize realistic motion, natural physics, detailed scene descriptions. Specify duration (3-30 seconds), camera movements, lighting changes over time, and narrative progression.'
  };

  try {
    const openai = new OpenAI({ 
      apiKey: effectiveApiKey,
      dangerouslyAllowBrowser: true 
    });

    const response = await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: `Optimize this prompt specifically for ${platform}. ${platformGuidelines[platform]} Return only the optimized prompt.`
        },
        {
          role: "user",
          content: prompt
        }
      ],
      max_tokens: 500,
      temperature: 0.7
    });

    return response.choices[0]?.message?.content || null;
  } catch (error) {
    console.error('Platform optimization failed:', error);
  }

  return null;
}